import { useEffect, useState } from "react"
import { HStack, Separator, Spinner } from "@chakra-ui/react"
import { downloadDocument } from "@/api/documents"
import { Button, Section, Text, toaster } from "@/components"
import { BcDocumentRequirements, NdaDocumentRequirements } from "@/features/client/compliance/components"
import { ALL_STEPS, BC_FORM_STEP_INDEX, SIGN_CONTRACT_STEP_INDEX } from "@/constants"
import { useDropboxDocuments, useUploadDocuments } from "@/hooks"
import { Deal } from "@/types"
import { DocumentsTable, DropZoneContainer } from "./components"

interface Props {
  isLoading: boolean
  deals: Deal[]
}

const WHITE_LISTED_FILE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword"
]

const PRE_OFFER_DOC_TYPES = [
  "Option Grant Information",
  "FMV (409A) Confirmation",
  "Stock Certificate",
]

const POST_BC_DOC_TYPES = [
  "ID - Passport | Driver's License",
  "Stock Option Plan",
  "Stock Option Grant"
]

export const DocumentsComponent = ({ isLoading, deals }: Props) => {
  const [currentDeal, setCurrentDeal] = useState<Deal | null>(deals[0] || null);
  const [stage, setStage] = useState<"PRE_OFFER" | "POST_BC" | "CLOSED">("CLOSED");
  const [docTypes, setDocTypes] = useState<string[]>(PRE_OFFER_DOC_TYPES);
  const [currentDocType, setCurrentDocType] = useState<string>(PRE_OFFER_DOC_TYPES[0]);
  const { mutate: uploadDocuments, isPending, isSuccess } = useUploadDocuments();

  useEffect(() => {
    if (!currentDeal && deals && deals.length > 0) {
      setCurrentDeal(deals[0]);
    }
  }, [deals]);

  useEffect(() => {
    if (currentDeal) {
      const index = ALL_STEPS.findIndex(step => step.step === currentDeal.portalStage);
      if (index >= SIGN_CONTRACT_STEP_INDEX) {
        setStage("CLOSED");
        return;
      }

      if (index < BC_FORM_STEP_INDEX) {
        setStage("PRE_OFFER");
        setDocTypes(PRE_OFFER_DOC_TYPES);
        setCurrentDocType(PRE_OFFER_DOC_TYPES[0]);
      } else {
        setStage("POST_BC");
        setDocTypes(POST_BC_DOC_TYPES);
        setCurrentDocType(POST_BC_DOC_TYPES[0]);
      }
    }
  }, [currentDeal]);

  const { data: dropboxDocuments, isLoading: isLoadingDocs } = useDropboxDocuments(currentDeal?.id ?? undefined);

  const handleUploadDocuments = async (filesToUpload: File[]) => {
    if (!currentDeal) return;
    for (let i = 0; i < filesToUpload.length; i++) {
      const fileToUpload = filesToUpload[i]
      if (!WHITE_LISTED_FILE_TYPES.includes(fileToUpload.type)) {
        toaster.create({
          title: `${fileToUpload.type} is not a valid file type`,
          type: "error",
          closable: true,
          duration: 3000,
        })
        return
      }
    }
    
    uploadDocuments(
      { documentType: currentDocType, dealIdStr: currentDeal.id.toString(), files: filesToUpload },
      {
        onSuccess: () => {
          toaster.create({
            title: "Files uploaded successfully",
            type: "success",
            duration: 3000,
          });
        },
        onError: () => {
          toaster.create({
            title: "Upload failed. Please contact your deal manager.",
            type: "error",
            duration: 3000,
          });
        },
      }
    )
  }

  const handleDownloadDocument = async (name: string) => {
    try {
      if (!currentDeal?.id) return;
      const blob = await downloadDocument({ dealId: currentDeal.id, name });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error in useDownloadDocument:", error);
      throw error;
    }
  }

  const renderDocumentsTable = () => {
    if (!currentDeal) {
      return null
    }

    if (isLoadingDocs || isLoading) {
      return (
        <Spinner />
      )
    }

    return (
      <DocumentsTable documents={dropboxDocuments} handleDownloadDocument={handleDownloadDocument} />
    )
  }

  return (
    <Section>
      <Text fontSize="2xl" fontWeight="bold" fontFamily="body">
        Documents
      </Text>
      <HStack my={3} gap={2}>
        {
          isLoading && (
            <Spinner />
          )
        }
        {deals.map((deal, index) => (
          <Button
            key={index}
            variant={currentDeal?.id === deal.id ? "solid" : "outline"}
            onClick={() => setCurrentDeal(deal)}
          >
            {deal.company.name}
          </Button>
        ))}
      </HStack>
      <Separator mb={5} />
      { stage === "PRE_OFFER" && <NdaDocumentRequirements /> }
      { stage === "POST_BC" && <BcDocumentRequirements /> }
      {
        stage !== "CLOSED" && (
          <>
            <Separator my={5} />
            <Text fontSize="sm">
              Document Type:
            </Text>
            <HStack mb={5}>
              {
                docTypes.map((docType, index) => (
                  <Button
                    key={index}
                    variant={currentDocType === docType ? "solid" : "outline"}
                    onClick={() => setCurrentDocType(docType)}
                  >
                    {docType}
                  </Button>
                ))
              }
            </HStack>
            <DropZoneContainer
              uploadDocuments={handleUploadDocuments}
              isLoading={isPending}
              isSuccess={isSuccess}
            />
          </>
        )
      }
      { renderDocumentsTable() }
    </Section>
  )
}
